import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {from, Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import moment from 'moment';
import {environment} from '../../../../../../environments';
import {OrganizationManagementService} from '../../_zen-legacy-common/zen-common-services/_services/organization-management.service';
import {DownloadService} from '../../_zen-legacy-common/zen-common-services/_services/download.service';
import {
  BillOverlapResponse,
  ElecBillDto,
  ElecBillCreateRequest,
  ElecBillUpdateRequest,
  PhysicalBillType
} from '../../_model/bills-v4.model';
import {CommodityType} from '../../_zen-legacy-common/_models/commodity';
import {PageableModel} from '../../../_modules/portfolio/_model/pageable.model';
import {getParams} from '../../_zen-legacy-common/_utils/set-param-util';

// Sets the literal commodity discriminator so BillDto narrowing works on top-level `commodityType`.
const withElecCommodity = (b: ElecBillDto): ElecBillDto => b ? {...b, commodityType: CommodityType.Electricity} : b;

@Injectable({providedIn: 'root'})
export class BillsV4ElecService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(
    private http: HttpClient,
    private orgMgtSvc: OrganizationManagementService,
    private downloadService: DownloadService,
  ) {}

  private root(customerId: number, serviceAddressId: number, meterId: number): string {
    return `${this.baseUrl}/v4/organizations/${this.orgMgtSvc.getOrganizationId()}` +
      `/customers/${customerId}/service-addresses/${serviceAddressId}/meters/${meterId}`;
  }

  getBillsByMeterId(customerId: number, serviceAddressId: number, meterId: number,
                    size: number, sort = 'generalInformation.billEndDate,desc'): Observable<PageableModel<ElecBillDto>> {
    const httpParams = getParams({size, sort});
    return this.http.get<PageableModel<ElecBillDto>>(
      `${this.root(customerId, serviceAddressId, meterId)}/bills-elec`,
      {params: httpParams},
    ).pipe(
      map(page => ({...page, content: page?.content?.map(withElecCommodity) ?? page?.content})),
      take(1),
    );
  }

  createBills(customerId: number, serviceAddressId: number, meterId: number,
              request: ElecBillCreateRequest): Observable<ElecBillDto> {
    return this.http.post<ElecBillDto>(
      `${this.root(customerId, serviceAddressId, meterId)}/bills-elec`,
      request,
    ).pipe(map(withElecCommodity), take(1));
  }

  updateBills(customerId: number, serviceAddressId: number, meterId: number, billId: number,
              body: Partial<Omit<ElecBillUpdateRequest, 'id'>>): Observable<ElecBillDto> {
    const headers = new HttpHeaders({'Content-Type': 'application/merge-patch+json'});
    return this.http.patch<ElecBillDto>(
      `${this.root(customerId, serviceAddressId, meterId)}/bills-elec/${billId}`,
      body, {headers},
    ).pipe(map(withElecCommodity), take(1));
  }

  updateBillFile(customerId: number, serviceAddressId: number, meterId: number,
                 billId: number, tempFileId: string, physicalBillType?: PhysicalBillType): Observable<ElecBillDto> {
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    const params = physicalBillType != null ? getParams({physicalBillType}) : undefined;
    return this.http.put<ElecBillDto>(
      `${this.root(customerId, serviceAddressId, meterId)}/bills-elec/${billId}/file`,
      JSON.stringify(tempFileId), {headers, params},
    ).pipe(map(withElecCommodity), take(1));
  }

  getBillDetailsById(customerId: number, serviceAddressId: number, meterId: number,
                     billId: number): Observable<ElecBillDto> {
    return this.http.get<ElecBillDto>(
      `${this.root(customerId, serviceAddressId, meterId)}/bills-elec/${billId}`,
    ).pipe(map(withElecCommodity), take(1));
  }

  deleteBills(customerId: number, serviceAddressId: number, meterId: number,
              billId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.root(customerId, serviceAddressId, meterId)}/bills-elec/${billId}`,
    ).pipe(take(1));
  }

  bulkDeleteBills(customerId: number, serviceAddressId: number, meterId: number,
                  billIds: number[]): Observable<void> {
    return this.http.delete<void>(
      `${this.root(customerId, serviceAddressId, meterId)}/bills-elec`,
      {body: billIds},
    ).pipe(take(1));
  }

  validateBillDates(customerId: number, serviceAddressId: number, meterId: number,
                    billStartDate: moment.Moment, billEndDate: moment.Moment,
                    billId?: number): Observable<BillOverlapResponse> {
    const httpParams = getParams({
      billStartDate: moment(billStartDate).format('YYYY-MM-DD'),
      billEndDate: moment(billEndDate).format('YYYY-MM-DD'),
      billId,
    });
    return this.http.get<BillOverlapResponse>(
      `${this.root(customerId, serviceAddressId, meterId)}/bill-elec-dates`,
      {params: httpParams},
    ).pipe(take(1));
  }

  downloadBill(customerId: number, serviceAddressId: number, meterId: number,
               billId: number, fileName?: string): Observable<void> {
    const url = `${this.root(customerId, serviceAddressId, meterId)}/bills-elec/${billId}/file`;
    return from(this.downloadService.downloadFile(url, fileName ?? `bill_${billId}.pdf`));
  }

  downloadBillByTypeUrl(customerId: number, serviceAddressId: number, meterId: number,
                       billId: number, physicalBillType?: PhysicalBillType): string {
    let url = `${this.root(customerId, serviceAddressId, meterId)}/bills-elec/${billId}/file`;
    if (physicalBillType != null) {
      url += `?physicalBillType=${physicalBillType}`;
    }
    return url;
  }

  deleteBillFile(customerId: number, serviceAddressId: number, meterId: number,
                 billId: number, physicalBillType: PhysicalBillType): Observable<void> {
    const httpParams = getParams({physicalBillType});
    return this.http.delete<void>(
      `${this.root(customerId, serviceAddressId, meterId)}/bills-elec/${billId}/file`,
      {params: httpParams},
    ).pipe(take(1));
  }
}
